import React, { useEffect, useMemo, useState } from 'react'
import { api, archiveSurvey, getSchoolOptions, postStudent, putStudent } from '../shared/ApiProvider'
import MatchedStudent from './StudentFieldComponents/MatchedStudent'
import { Button } from 'react-bootstrap'
import { emptyOptions, emptyStudent, emptySurvey, grades } from './assets/default-objects'
import Select from 'react-select'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import UpdateLanguages from './StudentFieldComponents/UpdateLanguages'

const studentValidationSchema = Yup.object().shape({
  // Required fields with length constraints matching backend
  firstName: Yup.string().max(50, 'First name must be 50 characters or less').trim(),

  lastName: Yup.string().max(50, 'Last name must be 50 characters or less').trim(),

  sex: Yup.string().oneOf(['M', 'F'], 'Sex must be M or F').length(1, 'Sex must be exactly 1 character'),

  birthDate: Yup.date().max(new Date(), 'Birth date cannot be in the future').typeError('Birth date must be a valid date'),

  // Optional fields with constraints
  bornInUS: Yup.boolean().nullable(),

  firstUSEnrollmentDate: Yup.date()
    .max(new Date(), 'Enrollment date cannot be in the future')
    .when('birthDate', ([birthDate], schema) => {
      return birthDate ? schema.min(birthDate, 'Enrollment date must be after birth date') : schema
    })
    .nullable()
    .typeError('First US enrollment date must be a valid date'),

  enrollingAt: Yup.string()
    .max(5, 'School code must be 5 characters or less')
    .matches(/^[A-Z0-9]*$/, 'School code must contain only uppercase letters and numbers')
    .nullable(),

  enrollingAtDate: Yup.string()
    .max(9, 'School year must be 9 characters or less')
    .matches(/^\d{4}-\d{4}$/, 'School year must be in format YYYY-YYYY')
    .test('valid-year-range', 'Invalid year range', function (value) {
      if (!value) return true // Allow empty
      const [start, end] = value.split('-').map(Number)
      return end === start + 1
    })
    .nullable(),

  gradeEntering: Yup.string()
    .max(4, 'Grade must be 4 characters or less')
    .oneOf(['PK', 'K', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', ''], 'Invalid grade selection')
    .nullable(),

  comment: Yup.string().max(500, 'Comment must be 500 characters or less').nullable(),

  permNumber: Yup.number().positive('PERM number must be positive').integer('PERM number must be an integer').nullable().typeError('PERM number must be a number'),
})
const normalizeWithTemplate = (template, api) => {
  if (Array.isArray(template)) {
    return api ?? template
  }

  if (template && typeof template === 'object') {
    const out = {}
    for (const key of Object.keys(template)) {
      out[key] = normalizeWithTemplate(template[key], api?.[key])
    }
    return out
  }

  return api ?? template
}

const UpdateStudent = () => {
  const [loading, setLoading] = useState(true)
  const [newStudent, setNewStudent] = useState()

  // Extract ID from URL path once using useMemo to prevent recalculation
  const id = useMemo(() => {
    const pathParts = window.location.pathname.split('/').filter(Boolean)
    return pathParts[pathParts.length - 1]
  }, []) // Empty deps - only calculate once on mount

  const [matchedStudent, setMatchedStudent] = useState()
  const [surveyResponse, setSurveyResponse] = useState(emptySurvey)
  // this is the target form data - this is what will be updated
  const [student, setStudent] = useState(emptyStudent)
  const [schools, setSchools] = useState(emptyOptions)
  const parseISODate = date => {
    const [yyyy, mm, dd] = date.trim().split('-')
    return new Date(yyyy, mm - 1, dd)
  }
  const getSchoolYear = date => {
    if (!date) return
    date = typeof date === 'string' ? parseISODate(date) : date
    const startYear = date.getMonth() >= 6 ? date.getFullYear() : date.getFullYear() - 1
    return `${startYear}-${startYear + 1}`
  }
  useEffect(() => {
    if (!id) {
      console.error('No survey ID provided')
      return
    }

    setLoading(true)
    getSchoolOptions().then(response => {
      setSchools(response)
    })
    api
      .get(`/surveyandstudent/${id}`)
      .then(response => {
        const {
          data: { surveyResponse, student, matchedStudent },
        } = response
        surveyResponse.enrollingSchoolYear = getSchoolYear(surveyResponse.enrollingAtDate)
        // todo: probably move matchedStudent to a separate call?
        setMatchedStudent(matchedStudent)
        // sort language contexts by priority
        surveyResponse.languageContexts.sort((a, b) => a.context.priority - b.context.priority)
        setSurveyResponse(surveyResponse)
        if (student) {
          if (student.permNumber === null) {
            setNewStudent(true)
            student.permNumber = surveyResponse.permNumber
            student.languageContexts = []
          }
          // need to inject options for Select component
          setStudent({
            // this should replace nulls with empty strings to make formik happy
            ...normalizeWithTemplate(emptyStudent, student),
            gradeEnteringOpt: grades.find(g => g.value === student.gradeEntering),
            enrollingAtOpt: schools.find(g => g.value === student.enrollingAt),
          })
        }
        setLoading(false)
      })
      .catch(error => {
        console.error('Error fetching survey data:', error)
        console.error('Error details:', error.response?.status, error.response?.data)
        setLoading(false)
        if (error.response?.status === 401) {
          alert('Authentication required. Please sign in and try again.')
        } else {
          alert(`Failed to load survey: ${error.message}`)
        }
      })
  }, [id])
  const handleFormikSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = Object.keys(emptyStudent).reduce((acc, key) => {
      if (Object.hasOwn(values, key)) {
        acc[key] = values[key]
      }
      return acc
    }, {})
    const saveStudent = newStudent ? postStudent(payload) : putStudent(payload)
    saveStudent
      .then(r => {
        return archiveSurvey(id)
      })
      .then(() => {
        resetForm()
      })
      .catch(error => {
        console.error('An error occurred:', error)
        alert(`Failed to save student: ${error.message || 'Unknown error'}`)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }
  return (
    <Formik
      initialValues={{
        ...student,
        'toggle-all': false,
        'check-firstName': false,
        'check-lastName': false,
        'check-sex': false,
        'check-birthDate': false,
        'check-bornInUS': false,
        'check-firstUSEnrollmentDate': false,
        'check-enrollingAt': false,
        'check-enrollingAtDate': false,
        'check-gradeEntering': false,
      }}
      validationSchema={studentValidationSchema}
      enableReinitialize={true}
      onSubmit={handleFormikSubmit}
      validateOnChange={true}
      validateOnBlur={true}>
      {({ values, setFieldValue, errors, touched, handleChange, isSubmitting }) => {
        const hasBlockingErrors = Object.keys(errors).some(key => touched[key])
        const selectedSchoolOpt = useMemo(() => {
          const id = values.enrollingAt
          if (id == null) return null
          return schools.find(o => String(o.value) === String(id)) ?? null
        }, [values.enrollingAt, schools])
        const useSurveyValue = ({ target: { name } }) => {
          const key = name.split('-')[1]
          const checked = !values[name]
          if (key === 'enrollingAtDate') {
            setFieldValue(key, checked ? surveyResponse['enrollingSchoolYear'] : (student[key] ?? ''))
            return
          }
          const val = checked ? surveyResponse[key] : (student[key] ?? '')
          setFieldValue(key, val)
          if (key === 'gradeEntering') {
            setFieldValue(
              'gradeEnteringOpt',
              grades.find(g => g.value === val)
            )
          }
          if (key === 'enrollingAt') {
            setFieldValue(
              'enrollingAtOpt',
              schools.find(g => g.value === val)
            )
          }
        }
        const resetCheckbox = ({ target: { name } }) => {
          if (`check-${name}` in values) setFieldValue(`check-${name}`, false)
        }
        const toggleAll = ({ target: { name } }) => {
          const checked = !values[name]
          Object.keys(values).forEach(k => {
            if (k.startsWith('check-'))
              setFieldValue(k, checked).then(() => {
                useSurveyValue({ target: { name: k } })
              })
          })
        }
        return (
          <>
            <div>
              {loading ? 'loading...' : ''}
              {newStudent ? <div>new student</div> : ''}
            </div>
            <MatchedStudent matchedStudent={matchedStudent} />
            <Form className="every-other-child-color">
              <div className="row align-items-center text-white bg-primary p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="toggle-all"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      toggleAll(e)
                    }}
                  />
                </div>
                <h5 className="col">Field</h5>
                <h5 className="col">Survey Data</h5>
                <h5 className="col">Student Data</h5>
              </div>

              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-firstName"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-firstName" className="col">
                  <strong>First Name</strong>
                </label>
                <div className="col">{surveyResponse.firstName}</div>
                <div className="col">
                  <Field
                    name="firstName"
                    required={true}
                    className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                    onChange={e => {
                      handleChange(e)
                      resetCheckbox(e)
                    }}
                  />
                  {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-lastName"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-lastName" className="col">
                  <strong>Last Name</strong>
                </label>
                <div className="col">{surveyResponse.lastName}</div>
                <div className="col">
                  <Field
                    name="lastName"
                    required={true}
                    className={`col form-control ${errors.lastName ? 'is-invalid' : ''}`}
                    onChange={e => {
                      handleChange(e)
                      resetCheckbox(e)
                    }}
                  />
                  {errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-sex"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-sex" className="col">
                  <strong>Sex</strong>
                </label>
                <div className="col">{surveyResponse.sex}</div>
                <div className="col">
                  <div>
                    <Field
                      id="sex-M"
                      type="radio"
                      name="sex"
                      value="M"
                      className="radio-size"
                      onChange={e => {
                        handleChange(e)
                        resetCheckbox(e)
                      }}
                    />
                    <label className="mx-1" htmlFor="sex-M">
                      Male
                    </label>
                    <span className="mx-3"></span>
                    <Field
                      id="sex-F"
                      type="radio"
                      name="sex"
                      value="F"
                      className="radio-size"
                      onChange={e => {
                        handleChange(e)
                        resetCheckbox(e)
                      }}
                    />
                    <label className="mx-1" htmlFor="sex-F">
                      Female
                    </label>
                  </div>
                  {errors.sex && <div className="invalid-feedback d-block">{errors.sex}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-birthDate"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-birthDate" className="col">
                  <strong>Birth Date</strong>
                </label>
                <div className="col">{surveyResponse.birthDate ? parseISODate(surveyResponse.birthDate).toLocaleDateString() : ''}</div>
                <div className="col">
                  <Field
                    name="birthDate"
                    required={true}
                    type={'date'}
                    className={`form-control ${errors.birthDate ? 'is-invalid' : ''}`}
                    onChange={e => {
                      handleChange(e)
                      resetCheckbox(e)
                    }}
                  />
                  {errors.birthDate && <div className="invalid-feedback d-block">{errors.birthDate}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-bornInUS"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-bornInUS" className="col">
                  <strong>Born in US or PR?</strong>
                </label>
                <div className="col">{surveyResponse?.bornInUS === null ? '--' : surveyResponse.bornInUS ? 'Yes' : 'No'}</div>
                <div className="col">
                  <Field
                    id="bornInUS-Y"
                    type="radio"
                    name="bornInUS"
                    checked={values.bornInUS === true}
                    className="radio-size"
                    onChange={e => {
                      setFieldValue('bornInUS', true)
                    }}
                  />
                  <label className="mx-1" htmlFor="bornInUS-Y">
                    Yes
                  </label>
                  <span className="mx-4"></span>
                  <Field
                    id="bornInUS-N"
                    type="radio"
                    name="bornInUS"
                    checked={values.bornInUS === false}
                    className="radio-size"
                    onChange={e => {
                      setFieldValue('bornInUS', false)
                    }}
                  />
                  <label className="mx-1" htmlFor="bornInUS-N">
                    No
                  </label>
                  {errors.bornInUS && <div className="invalid-feedback d-block">{errors.bornInUS}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-firstUSEnrollmentDate"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-firstUSEnrollmentDate" className="col">
                  <strong>First Enrollment Date (US)</strong>
                </label>
                <div className="col">{surveyResponse.firstUSEnrollmentDate ? parseISODate(surveyResponse.firstUSEnrollmentDate).toLocaleDateString() : ''}</div>
                <div className="col">
                  <Field
                    name="firstUSEnrollmentDate"
                    type={'date'}
                    className={`form-control ${errors.firstUSEnrollmentDate ? 'is-invalid' : ''}`}
                    onChange={e => {
                      handleChange(e)
                      resetCheckbox(e)
                    }}
                  />
                  {errors.firstUSEnrollmentDate && <div className="invalid-feedback d-block">{errors.firstUSEnrollmentDate}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-enrollingAt"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-enrollingAt" className="col">
                  <strong>Enrolling At (School Code)</strong>
                </label>
                <div className="col">{surveyResponse.enrollingAt}</div>
                <div className="col">
                  <Field name="enrollingAtOpt" className={'form-control'}>
                    {() => (
                      <Select
                        name="enrollingAtOpt"
                        value={selectedSchoolOpt}
                        onChange={option => {
                          setFieldValue('enrollingAt', option.value)
                        }}
                        options={schools}
                        error={errors.enrollingAt}
                      />
                    )}
                  </Field>
                  {errors.enrollingAt && <div className="invalid-feedback d-block">{errors.enrollingAt}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-enrollingAtDate"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-enrollingAtDate" className="col">
                  <strong>Year for Current School</strong>
                </label>
                <div className="col" style={{ fontSize: '.8rem' }}>
                  School Year: {surveyResponse.enrollingSchoolYear}
                  <br />
                  Specific Date: {surveyResponse.enrollingAtDate}
                </div>
                <div className="col">
                  <Field
                    name="enrollingAtDate"
                    className={`form-control ${errors.enrollingAtDate ? 'is-invalid' : ''}`}
                    onChange={e => {
                      handleChange(e)
                      resetCheckbox(e)
                    }}
                  />
                  {errors.enrollingAtDate && <div className="invalid-feedback d-block">{errors.enrollingAtDate}</div>}
                </div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="form-check col-1 ml-2">
                  <Field
                    name="check-gradeEntering"
                    type="checkbox"
                    className="form-check-input compare-checkbox-select"
                    onChange={e => {
                      handleChange(e)
                      useSurveyValue(e)
                    }}
                  />
                </div>
                <label htmlFor="check-gradeEntering" className="col">
                  <strong>Entering Grade</strong>
                </label>
                <div className="col">{surveyResponse.gradeEntering}</div>
                <div className="col">
                  <Field name="gradeEnteringOpt" className={'form-control'}>
                    {({ field }) => (
                      <Select
                        name={field.name}
                        value={field.value}
                        onChange={option => {
                          setFieldValue('gradeEntering', option.value)
                          setFieldValue(field.name, option)
                        }}
                        options={grades}
                        error={errors.gradeEntering}
                      />
                    )}
                  </Field>
                </div>
              </div>

              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="col-1 "></div>
                <label className="col">
                  <strong>Was Screened and Identified as EL</strong>
                </label>
                <div className="col">{surveyResponse?.wasScreened === null ? '--' : surveyResponse.wasScreened ? 'true' : 'false'}</div>
                <div className="col"></div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="col-1 "></div>
                <label className="col">
                  <strong>Was Identified as an Immigrant</strong>
                </label>
                <div className="col">{surveyResponse?.isImmigrant === null ? '--' : surveyResponse.isImmigrant ? 'true' : 'false'}</div>
                <div className="col"></div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="col-1 "></div>
                <label className="col">
                  <strong>Comments From Survey</strong>
                </label>
                <div className="col">{surveyResponse?.comment}</div>
                <Field name="comment" className={`col form-control ${errors.comment && touched.comment ? 'is-invalid' : ''}`} />
                {errors.comment && touched.comment && <div className="invalid-feedback d-block">{errors.comment}</div>}
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="col-1 "></div>
                <label className="col">
                  <strong>Permnumber</strong>
                </label>
                <div className="col"></div>
                <div className="col">{values.permNumber}</div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="col-1 "></div>
                <label className="col">
                  <strong>Contact</strong>
                </label>
                <div className="col">{surveyResponse.screenerName}</div>
              </div>
              <div className="row align-items-center p-1">
                <div style={{ width: '25px', height: '40px' }} className="col-1 "></div>
                <label className="col">
                  <strong>Survey Entered</strong>
                </label>
                <div className="col">{surveyResponse.entryDate ? new Date(surveyResponse.entryDate).toLocaleString() : ''}</div>
              </div>
              <hr />
              <UpdateLanguages surveyResponse={surveyResponse} studentLanguages={values.languageContexts} />

              <div className="row justify-content-center m-3">
                <Button
                  disabled={isSubmitting || hasBlockingErrors}
                  variant="secondary"
                  as="input"
                  type="submit"
                  value={isSubmitting ? 'Submitting...' : hasBlockingErrors ? 'Fix Errors to Submit' : 'Submit and Archive Survey'}
                />
              </div>
            </Form>
          </>
        )
      }}
    </Formik>
  )
}

export default UpdateStudent
